import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ApiError, type UserRole, type components } from '@shift/contracts';
import { apiClient, registerSessionHooks } from '../../shared/api/client.js';
import { isCorporate } from './roles.js';

export interface SessionUser {
  id: string;
  displayName: string;
  email: string;
  roles: UserRole[];
}

const ACCESS_TOKEN_KEY = 'shift.accessToken';

/**
 * `sessionStorage` is not always there to be used: a browser in a private mode,
 * or one configured to block site data, throws on access rather than returning
 * null. Losing the stored token costs a sign-in; an uncaught throw here costs a
 * white screen, because this runs while the store is being created.
 */
const storedToken = (): string | undefined => {
  try {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

const rememberToken = (token: string | undefined): void => {
  try {
    if (token === undefined) sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    else sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch {
    // The session still works for this page; only the reload does not.
  }
};

/**
 * Owns the access token and the signed-in user.
 *
 * The refresh token lives in an httpOnly cookie the app never reads; the access
 * token is kept in `sessionStorage` so a reload has something to start from,
 * and is short-lived enough that this is a reasonable trade.
 */
export const useSessionStore = defineStore('session', () => {
  const accessToken = ref<string | undefined>(storedToken());
  const user = ref<SessionUser | null>(null);

  const isAuthenticated = computed(() => accessToken.value !== undefined && user.value !== null);
  const roles = computed<UserRole[]>(() => user.value?.roles ?? []);
  const corporate = computed(() => isCorporate(roles.value));

  const hasAnyRole = (allowed: readonly UserRole[]): boolean =>
    roles.value.some((role) => allowed.includes(role));

  const signIn = (session: { accessToken: string; user: SessionUser }): void => {
    accessToken.value = session.accessToken;
    user.value = session.user;
    rememberToken(session.accessToken);
  };

  /**
   * Trades the refresh cookie for a new access token.
   *
   * Single-flight: several requests can 401 at the same moment — a page that
   * loads three queries at once is the ordinary case — and each of them asking
   * for a new token would rotate the refresh token three times, and the
   * rotation detector treats a reused refresh token as theft and revokes the
   * whole chain. So they all wait on the first attempt.
   */
  let inFlight: Promise<boolean> | undefined;

  const refresh = async (): Promise<boolean> => {
    inFlight ??= (async () => {
      try {
        const session = await apiClient.post<components['schemas']['SessionResponseDto']>(
          '/auth/refresh',
          // Marked a retry so a refresh that itself 401s does not ask to
          // refresh again.
          { body: {}, headers: { 'x-shift-client': 'web' }, isRetry: true },
        );
        accessToken.value = session.accessToken;
        rememberToken(session.accessToken);
        return true;
      } catch (error) {
        // Only a refusal ends the session. A 401 means the refresh token is
        // expired, revoked, or was reused and the chain revoked with it —
        // nothing on this device can recover from that.
        //
        // Anything else is the network or the server having a moment: a 502
        // from a proxy mid-deploy, a dropped connection, a timeout. The cookie
        // is still good, so signing out there would end a working session over
        // a blip and make the user type their password for nothing.
        if (error instanceof ApiError && error.status === 401) forgetSession();
        return false;
      } finally {
        inFlight = undefined;
      }
    })();

    return inFlight;
  };

  /**
   * Rebuilds the session at start-up.
   *
   * The access token survived the reload in `sessionStorage`; the user did not,
   * and `isAuthenticated` needs both — so every refresh of the page signed
   * people out while they were holding a perfectly good refresh cookie.
   *
   * Read from `/auth/me` rather than decoded from the token, so a role changed
   * or an account disabled since it was issued takes effect now. A 401 here is
   * handled by the client, which refreshes and retries before this sees it.
   */
  /**
   * `restore()`, run at most once and awaitable from anywhere.
   *
   * The router installs itself and starts navigating immediately, before
   * `main.ts` gets to call `restore` — so the guard used to decide while the
   * session was still empty, bounce a signed-in user to the login page, and let
   * the restore finish a moment later against a page nobody was on any more.
   * The guard awaits this instead of trusting the order things happened to
   * start in.
   */
  let restoring: Promise<void> | undefined;
  const whenRestored = (): Promise<void> => (restoring ??= restore());

  const restore = async (): Promise<void> => {
    if (user.value !== null) return;
    // No token at all still leaves a refresh cookie worth trying: sessionStorage
    // is per-tab, so opening a second tab starts with nothing.
    if (accessToken.value === undefined && !(await refresh())) return;

    try {
      const me = await apiClient.get<components['schemas']['SessionUserDto']>('/auth/me');
      user.value = {
        id: me.id,
        displayName: me.displayName,
        email: me.email ?? '',
        roles: me.roles as UserRole[],
      };
    } catch {
      forgetSession();
    }
  };

  /**
   * Picks up a role the account gained after signing in.
   *
   * Roles travel in the access token, so registering a company — which grants
   * `CORPORATE_CLIENT` — leaves the token in hand describing an account that no
   * longer exists in that form. Re-issuing it is what makes the new role real
   * to the guard on the very next request, and re-reading `/auth/me` is what
   * makes it real to this app.
   */
  const refreshProfile = async (): Promise<void> => {
    if (!(await refresh())) return;

    try {
      const me = await apiClient.get<components['schemas']['SessionUserDto']>('/auth/me');
      user.value = {
        id: me.id,
        displayName: me.displayName,
        email: me.email ?? '',
        roles: me.roles as UserRole[],
      };
    } catch {
      // The token was just re-issued, so a failure here is the network rather
      // than the session. Leaving the profile as it was is better than signing
      // somebody out over a blip.
    }
  };

  /** Drops what this tab holds. The server session, if any, is untouched. */
  const forgetSession = (): void => {
    accessToken.value = undefined;
    user.value = null;
    rememberToken(undefined);
  };

  /**
   * Ends a session at the server that this app never adopted.
   *
   * The wrong-app case: the credentials were right, the account simply does not
   * belong here, so no session is established locally — which means there is no
   * stored token for the logout call to authenticate with. The token that came
   * back from sign-in is passed explicitly, because it is the only one that
   * would work and the store deliberately never held it.
   */
  const discardServerSession = async (token: string): Promise<void> => {
    try {
      await apiClient.post('/auth/logout', {
        isRetry: true,
        headers: { authorization: `Bearer ${token}` },
      });
    } catch {
      // Nothing to recover; the refresh cookie is the server's to expire.
    } finally {
      forgetSession();
    }
  };

  /**
   * Ends the session, here and at the server.
   *
   * Clearing local state alone did not sign anybody out: the refresh token
   * lives in an httpOnly cookie this app cannot delete, so the next load traded
   * it for a fresh access token and the "signed out" user was signed back in.
   * `/auth/logout` revokes it, and existed all along with nothing calling it.
   *
   * `isRetry` because a 401 from logout means the session is already gone,
   * which is the outcome being asked for — refreshing to retry it would revive
   * the very thing being ended. The local half happens either way: a user who
   * clicked sign out must not stay signed in because the network was down.
   */
  const signOut = async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout', { isRetry: true });
    } catch {
      // Nothing to recover: the session is over on this device regardless.
    } finally {
      forgetSession();
    }
  };

  // The HTTP client asks the store for the token instead of importing it, which
  // would create a cycle: client -> store -> client.
  // A 401 means the access token expired, not that the session ended: the
  // refresh cookie outlives it thirty times over. Signing out here was why a
  // session lasted fifteen minutes.
  registerSessionHooks({
    getAccessToken: () => accessToken.value,
    onUnauthorized: () => refresh(),
  });

  return {
    accessToken,
    user,
    roles,
    isAuthenticated,
    restore,
    whenRestored,
    refreshProfile,
    isCorporate: corporate,
    hasAnyRole,
    signIn,
    signOut,
    forgetSession,
    discardServerSession,
  };
});

export { CLIENT_APP_ROLES } from './roles.js';
